import React, { useCallback, useState, useMemo } from 'react'
import { View, StyleSheet, ActivityIndicator, Text, TouchableOpacity, Clipboard } from 'react-native'
import { useFocusEffect, useNavigation } from '@react-navigation/native'
import moment from 'moment'

import { useAppDispatch, useAppSelector } from '#app/hooks'
import { useAppTheme } from '#app/theme'
import { Central } from '#app/types'
import { resetLoaderById, selectLoaderById, setLoaderStart, setLoaderSuccess } from '#app/loader'
import { selectUserCentralById } from '#app/centraluser'
import { Table } from '#app/components/Table'
import { SearchInput } from '#app/components/SearchInput'
import { selectApi } from '#app/api'

const ITEMS_PER_PAGE = 25

const truncateString = (str: string, maxLength: number = 10) => {
  if (str.length <= maxLength) return str
  return `${str.substring(0, 5)}...${str.substring(str.length - 5)}`
}

const SuperadminCentrals = () => {
  const dispatch = useAppDispatch()
  const [allCentrals, setAllCentrals] = useState<Array<Central>>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const navigation = useNavigation()
  const theme = useAppTheme()
  const api = useAppSelector(selectApi)

  const { isLoading } = useAppSelector(state => selectLoaderById(state, { id: 'centrals' }))

  const fetchAllCentrals = useCallback(async () => {
    try {
      dispatch(setLoaderStart({ id: 'centrals' }))
      let hasNextPage = true
      let after: string | undefined = undefined
      let allNodes: Array<Central> = []
      let pageCount = 0

      while (hasNextPage) {
        pageCount++
        const response = await api.getCentrals(after)
        if (!response.ok || !response.data) {
          throw new Error('Failed to fetch centrals')
        }
        const data = response.data

        allNodes = [...allNodes, ...data.edges.map(edge => ({
          ...edge.node,
          id: edge.node.UUID,
          name: edge.node.name || ''
        }))]
        hasNextPage = data.pageInfo.hasNextPage
        after = data.pageInfo.endCursor
      }

      setAllCentrals(allNodes)
      dispatch(setLoaderSuccess({ id: 'centrals' }))
    } catch (error) {
      console.error('Error fetching all centrals:', error)
    }
  }, [api, dispatch])

  useFocusEffect(useCallback(() => {
    fetchAllCentrals()
    return () => {
      resetLoaderById('centrals')
    }
  }, [fetchAllCentrals]))

  const handleCopyYggdrasilIP = useCallback((ip: string) => {
    Clipboard.setString(ip)
  }, [])

  const handleViewDetails = useCallback((central: Central) => {
    navigation.navigate('SuperadminCentralDetail', { id: central.UUID })
  }, [navigation])

  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (info: any) => {
        const central = info.row.original
        const uc = useAppSelector(state => selectUserCentralById(state, { id: central.UUID }))
        const centralName = central.name === '' ? `No Name <${central.UUID}>` : central.name
        return (
          <TouchableOpacity 
            style={styles.nameCell}
            onPress={() => handleViewDetails(central)}
          >
            <Text style={{ color: theme.textColor }}>
              {uc.name !== '' ? `${centralName} (${uc.name})` : centralName}
            </Text>
          </TouchableOpacity>
        )
      },
    },
    {
      accessorKey: 'yggdrasilIP',
      header: 'Yggdrasil IP',
      cell: (info: any) => {
        const ip = info.getValue()
        if (!ip) return '-'
        return (
          <View style={styles.ipCell}>
            <TouchableOpacity onPress={() => handleCopyYggdrasilIP(ip)}>
              <Text style={{ color: theme.colors.primary }} numberOfLines={1}>
                {truncateString(ip)}
              </Text>
            </TouchableOpacity>
          </View>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: (info: any) => (
        <View style={styles.dateCell}>
          <Text style={{ color: theme.textColor }}>
            {moment(info.getValue()).format('DD/MM/YY HH:mm')}
          </Text>
        </View>
      ),
    },
  ], [theme.colors.primary, theme.textColor, handleCopyYggdrasilIP, handleViewDetails])

  const filteredData = useMemo(() => {
    if (!searchQuery) return allCentrals
    const query = searchQuery.toLowerCase()
    return allCentrals.filter(central => {
      const name = central.name.toLowerCase()
      const yggdrasilIP = central.yggdrasilIP?.toLowerCase() || ''
      const uuid = central.UUID.toLowerCase()
      return name.includes(query) || yggdrasilIP.includes(query) || uuid.includes(query)
    })
  }, [allCentrals, searchQuery])

  // Calculate pagination values
  const startIndex = currentPage * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedData = filteredData.slice(startIndex, endIndex)

  if (isLoading) {
    return (
      <View style={[styles.container, styles.loadingContainer, { backgroundColor: theme.backgroundColor }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={[styles.loadingText, { color: theme.textColor }]}>
          Loading centrals...
        </Text>
      </View>
    )
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.backgroundColor }]}>
      <SearchInput
        value={searchQuery}
        onChangeText={setSearchQuery}
        placeholder="Search centrals..."
      />
      <Table
        data={paginatedData}
        columns={columns}
        isLoading={isLoading}
        totalCount={filteredData.length}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        actionsWidth={32}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  nameCell: {
    flex: 3,
    paddingRight: 8,
  },
  ipCell: {
    flex: 2,
    paddingRight: 8,
  },
  dateCell: {
    flex: 1.5,
    paddingRight: 8,
  },
})

export default SuperadminCentrals
